<template>
  <div class="component-wrapper secondary d-flex flex-column">
    <div class="d-flex align-center">
      <h1 class="white--text font-weight-bold text-h5 px-2">
        {{ $t("menu.achievements") }}
      </h1>
    </div>

    <div
      v-if="loader"
      class="d-flex flex-column align-center flex-grow-1"
      style="height: 500px"
    >
      <v-progress-circular indeterminate color="white" size="60">
      </v-progress-circular>
    </div>

    <v-expansion-panels v-else-if="!loader && displayAchievements.length > 0">
      <v-expansion-panel
        class="secondary elevation-0"
        v-for="route in displayAchievements"
        :key="route.id"
      >
        <v-expansion-panel-header>
          <template v-slot:actions>
            <v-icon color="white"> mdi-chevron-down </v-icon>
          </template>
          <div class="d-flex flex-column" style="gap: 6px">
            <div class="d-flex align-center" style="gap: 8px">
              <v-icon v-if="isCompleted(route)" color="success" small>
                mdi-check-circle
              </v-icon>
              <v-icon
                v-else-if="route?.multi_day && getMultiDayProgress(route).hasProgress"
                color="warning"
                small
              >
                mdi-progress-clock
              </v-icon>
              <span class="white--text elevation-0 font-weight-medium">
                {{ route.name }}
              </span>
            </div>

            <div class="d-flex flex-wrap align-center" style="gap: 6px">
              <v-chip
                v-if="getAreaName(route.area_id)"
                x-small
                outlined
                color="white"
                class="white--text"
              >
                {{ getAreaName(route.area_id) }}
              </v-chip>

              <template
                v-if="route?.multi_day && getMultiDayProgress(route).hasProgress"
              >
                <v-chip
                  v-for="d in getMultiDayProgress(route).days"
                  :key="`day-${route.id}-${d}`"
                  x-small
                  color="secondary"
                  class="white--text"
                >
                  Day {{ d }}
                </v-chip>
                <v-chip
                  v-if="getMultiDayProgress(route).total"
                  x-small
                  outlined
                  color="white"
                  class="white--text"
                >
                  {{ getMultiDayProgress(route).days.length }}/{{
                    getMultiDayProgress(route).total
                  }}
                </v-chip>
              </template>
            </div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content color="white">
          <v-img
            class="my-2"
            :aspect-ratio="1"
            :src="'https://iguide.mobics.gr/' + route.main_image"
          ></v-img>
          <span class="secondary--text" v-html="route.description"> </span>
          <v-btn
            @click="$router.push(`${$route.path}/${route.id}`)"
            color="secondary"
            outlined
            class="ml-auto"
            style="text-transform: none"
          >
            {{ $t("general.continue") }}
            <v-icon> mdi-arrow-right </v-icon>
          </v-btn>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <div
      class="white--text px-2"
      v-else-if="!loader && displayAchievements.length == 0"
    >
      {{ $t("validation.no_achievements") }}
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapState, mapActions } from "vuex";
export default {
  async created() {
    this.loader = true;

    await this.getAreas();

    const res = await axios.post(
      `${process.env.VUE_APP_BASE_URL}/clients/get-achievements`,
      {
        client_id: this.user_id,
      },
    );
    if (res.data?.achieved_paths) {
      this.achievements = res.data.achieved_paths;
    }
    if (res.data?.achieved_days) {
      this.achievedDays = res.data.achieved_days;
    }

    this.loader = false;
  },

  data() {
    return {
      achievements: [],
      achievedDays: [],
      loader: false,
    };
  },

  computed: {
    ...mapState({
      user_id: (state) => state.auth.user_id,
      areas: (state) => state.areas.areas,
    }),

    displayAchievements() {
      const byId = new Map();

      if (Array.isArray(this.achievements)) {
        for (const p of this.achievements) {
          if (p?.id != null) byId.set(p.id, p);
        }
      }

      if (Array.isArray(this.achievedDays)) {
        for (const entry of this.achievedDays) {
          const p = entry?.path;
          if (p?.id != null && !byId.has(p.id)) byId.set(p.id, p);
        }
      }

      return Array.from(byId.values());
    },

    completedPathIds() {
      const ids = new Set();
      if (Array.isArray(this.achievements)) {
        for (const p of this.achievements) {
          if (p?.id != null) ids.add(p.id);
        }
      }
      return ids;
    },
  },

  methods: {
    ...mapActions(["getAreas"]),

    getAreaName(areaId) {
      const area = this.areas.find((a) => a.id === areaId);
      return area ? area.name : "";
    },

    isCompleted(pathOrRoute) {
      const id = pathOrRoute?.id;
      return id != null && this.completedPathIds?.has?.(id);
    },

    getMultiDayProgress(pathOrRoute) {
      const pathId = pathOrRoute?.id;
      let total =
        pathOrRoute?.days?.length ?? pathOrRoute?.path?.days?.length ?? 0;

      if (!pathId || !Array.isArray(this.achievedDays)) {
        return { hasProgress: false, days: [], total: total || 0 };
      }

      const entriesForPath = this.achievedDays.filter((entry) => {
        const entryPathId =
          entry?.path?.id ?? entry?.path_id ?? entry?.path?.path_id;
        return entryPathId === pathId;
      });
      if (!entriesForPath.length) return { hasProgress: false, days: [], total };

      const daysSet = new Set();
      for (const entry of entriesForPath) {
        if (entry?.day_number != null) {
          const n = Number(entry.day_number);
          if (Number.isFinite(n)) daysSet.add(n);
        } else {
          // fallback for older payloads: 0-based order
          const order = Number(entry?.day?.order ?? entry?.order);
          if (Number.isFinite(order)) daysSet.add(order + 1);
        }
        if (!total && entry?.total_days != null) {
          const backendTotal = Number(entry.total_days);
          if (Number.isFinite(backendTotal) && backendTotal > 0) {
            total = backendTotal;
          }
        }
      }

      const days = Array.from(daysSet).sort((a, b) => a - b);
      const hasProgress = days.length > 0;

      if (!total) {
        total = entriesForPath[0]?.path?.days?.length ?? 0;
      }

      return { hasProgress, days, total };
    },
  },
};
</script>

<style></style>
